import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import display from 'lib/Display'
import BabaForm from 'components/forms/Index'
import CheckoutContext from 'components/bookings/contexts/CheckoutContext'
import NavigationButtons from 'components/bookings/NavigationButtons'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

const GiftCardPaymentForm = ({
  user,
  leftToPay,
  paymentErrorMessage,
  urls,
  order,
  authenticityToken
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const context = useContext(CheckoutContext)

  const [errors, setErrors] = useState(null)
  const [disableSubmitButton, setDisableSubmitButton] = useState(true)

  useEffect(() => {
    if (stripe && elements) setDisableSubmitButton(false)
  }, [stripe, elements])

  useEffect(() => {
    if (paymentErrorMessage) {
      setErrors([{ field: 'Erreur', message: paymentErrorMessage }])
    } else {
      setErrors([])
    }
  }, [paymentErrorMessage])

  const confirmPayment = async () => {
    if (!stripe || !elements) {
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: urls.returnURL
      }
    })

    fetch(urls.paymentErrorURL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        authenticity_token: authenticityToken,
        payable_id: order.id,
        payable_type: 'GiftCardOrder'
      })
    })
      .then(async (response) => {
        if (response.status === 404) {
          const errorData = await response.json().catch(() => ({}))
          console.error('Gift card order not found:', errorData)
          return null
        }
        if (!response.ok) {
          throw new Error(`${response.status}`)
        }
        if (response.status === 204) {
          return null
        }
      })
      .catch((error) => {
        console.error('Network or server error:', error)
      })

    if (error) {
      if (
        error.type === 'card_error' ||
        error.type === 'validation_error' ||
        error.type === 'invalid_request_error'
      ) {
        setErrors([{ field: 'Erreur', message: error.message }])
      } else {
        setErrors([
          {
            field: 'Erreur',
            message:
              'Une erreur est survenue lors du paiement, veuillez réessayer'
          }
        ])
      }
    }

    context.stopLoading?.()
  }

  const paymentElementOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false
    },
    defaultValues: {
      billingDetails: {
        name: `${user.fname} ${user.lname}`,
        email: user.email,
        phone: user.phone
      }
    },
    business: { name: 'Babasport' }
  }

  return (
    <div className="checkout-payment">
      <div className="step-title">Paiement</div>
      <div className="row">
        <div className="col-xs-12">
          <BabaForm.BabaErrors errors={errors} />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <PaymentElement
            id="payment-element"
            options={paymentElementOptions}
          />
          <div className="mt-2 notice">
            En validant ma commande, je déclare avoir pris connaissance et
            accepté les{' '}
            <a href={urls.aboutTermsURL} target="_blank" rel="noreferrer">
              conditions générales de vente
            </a>
            .
          </div>
          <NavigationButtons
            priceAfter={leftToPay}
            goPrevious={context.goPrevious}
            submitLabel={`Payer ${display.currency(leftToPay)}`}
            handleValidate={confirmPayment}
            disableSubmitButton={disableSubmitButton}
          />
        </div>
      </div>
    </div>
  )
}

GiftCardPaymentForm.propTypes = {
  user: PropTypes.object.isRequired,
  leftToPay: PropTypes.string.isRequired,
  paymentErrorMessage: PropTypes.string,
  urls: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
  authenticityToken: PropTypes.string.isRequired
}

export default GiftCardPaymentForm
